'use client';

import type { JSX } from '@emotion/react/jsx-runtime';
import type { AxiosError } from 'axios';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import type { Unauthenticated, Unauthorized } from '../../../lib/error';

interface State {
  hasError: boolean;
  error: Error | AxiosError | Unauthenticated | Unauthorized | undefined;
}

interface Props {
  children?: ReactNode;
  fallback: ReactNode;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props | Readonly<Props>) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  public static getDerivedStateFromError(error: any): State {
    return { hasError: true, error };
  }

  public async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NODE_ENV === 'production') {
      const Sentry = await import('@sentry/nextjs');
      Sentry.captureException(error, { data: { errorInfo } });
    }

    let errorStr;
    try {
      errorStr = JSON.stringify(error);
    } catch (e) {
      // noop
    }

    let errorInfoStr;
    try {
      errorInfoStr = JSON.stringify(errorInfo);
    } catch (e) {
      // noop
    }

    console.error('ErrorBoundary caught an error:', { error, errorInfo, errorStr, errorInfoStr });
  }

  public render() {
    const {
      state: { hasError, error },
      props: { fallback, children },
    } = this;

    if (hasError) {
      return addExtraProps(fallback, {
        error,
      });
    }

    return children;
  }
}

function addExtraProps(Component: any, extraProps: JSX.IntrinsicAttributes & { error: any }) {
  return (
    <Component.type
      {...Component.props}
      {...extraProps}
    />
  );
}
